import React, { useState, useEffect } from 'react';
import { Package, Search, Edit2, Check, X, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import Layout from '../components/Layout';
import { integracoesAPI } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'react-toastify';

const Produtos = () => {
  const { darkMode } = useTheme();
  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [total, setTotal] = useState(0);
  const [editando, setEditando] = useState(null);
  const [novaCategoria, setNovaCategoria] = useState('');

  const t1 = darkMode ? 'text-white' : 'text-gray-900';
  const t2 = darkMode ? 'text-gray-400' : 'text-gray-500';
  const card = darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200';

  const loadData = async () => {
    setLoading(true);
    try {
      const [prodRes, catRes, statsRes] = await Promise.all([
        integracoesAPI.listarProdutos({ categoria: filtroCategoria, busca, pagina }),
        integracoesAPI.categorias(),
        integracoesAPI.statsProdutos(),
      ]);
      setProdutos(prodRes.data?.data || []);
      setTotalPaginas(prodRes.data?.paginas || 1);
      setTotal(prodRes.data?.total || 0);
      setCategorias(catRes.data || []);
      setStats(statsRes.data || {});
    } catch { toast.error('Erro ao carregar produtos'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [filtroCategoria, pagina]);

  const buscar = () => { setPagina(1); loadData(); };

  const salvarCategoria = async (id) => {
    try {
      await integracoesAPI.atualizarCategoria(id, novaCategoria);
      setProdutos(prev => prev.map(p => p.id === id ? { ...p, categoria: novaCategoria } : p));
      setEditando(null);
      toast.success('Categoria atualizada');
    } catch { toast.error('Erro ao atualizar'); }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className={`text-2xl font-bold flex items-center gap-2 ${t1}`}>
            <Package size={24} /> Catálogo de Produtos
          </h1>
          <button onClick={loadData} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}>
            <RefreshCw size={16} className={`${t2} ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <div className={`rounded-lg p-3 border ${card}`}>
            <p className={`text-[10px] uppercase font-semibold ${t2}`}>Total produtos</p>
            <p className={`text-2xl font-bold ${t1}`}>{stats.total || 0}</p>
          </div>
          <div className={`rounded-lg p-3 border ${card}`}>
            <p className={`text-[10px] uppercase font-semibold ${t2}`}>Com estoque</p>
            <p className="text-2xl font-bold text-green-500">{stats.com_estoque || 0}</p>
          </div>
          <div className={`rounded-lg p-3 border ${card}`}>
            <p className={`text-[10px] uppercase font-semibold ${t2}`}>Categorias</p>
            <p className={`text-2xl font-bold ${t1}`}>{stats.total_categorias || 0}</p>
          </div>
          <div className={`rounded-lg p-3 border ${card}`}>
            <p className={`text-[10px] uppercase font-semibold ${t2}`}>Com categoria</p>
            <p className="text-2xl font-bold text-blue-500">{stats.com_categoria || 0}</p>
          </div>
          <div className={`rounded-lg p-3 border ${card}`}>
            <p className={`text-[10px] uppercase font-semibold ${t2}`}>Com embedding</p>
            <p className="text-2xl font-bold text-purple-500">{stats.com_embedding || 0}</p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar categorias */}
          <div className={`w-full lg:w-64 lg:flex-shrink-0 rounded-lg border p-4 ${card} max-h-[70vh] overflow-y-auto`}>
            <p className={`text-xs font-semibold mb-3 ${t2}`}>Categorias ({categorias.length})</p>
            <button onClick={() => { setFiltroCategoria(''); setPagina(1); }}
              className={`w-full text-left px-2 py-1.5 rounded text-xs mb-1 ${!filtroCategoria ? 'bg-blue-500 text-white' : darkMode ? 'hover:bg-zinc-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'}`}>
              Todos ({total})
            </button>
            {categorias.map((c, i) => (
              <button key={i} onClick={() => { setFiltroCategoria(c.categoria); setPagina(1); }}
                className={`w-full text-left px-2 py-1.5 rounded text-xs mb-0.5 flex justify-between ${filtroCategoria === c.categoria ? 'bg-blue-500 text-white' : darkMode ? 'hover:bg-zinc-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'}`}>
                <span className="truncate">{c.categoria}</span>
                <span className="opacity-60">{c.qtd}</span>
              </button>
            ))}
          </div>

          {/* Lista produtos */}
          <div className="flex-1">
            {/* Busca */}
            <div className="flex gap-2 mb-4">
              <div className="flex-1 relative">
                <Search size={14} className={`absolute left-3 top-2.5 ${t2}`} />
                <input value={busca} onChange={e => setBusca(e.target.value)} onKeyDown={e => e.key === 'Enter' && buscar()}
                  placeholder="Buscar produto..."
                  className={`w-full pl-9 pr-3 py-2 rounded-lg text-sm border ${darkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'border-gray-200'}`} />
              </div>
              <button onClick={buscar} className="gradient-button px-4 py-2 text-sm text-white rounded-lg">Buscar</button>
            </div>

            {/* Tabela */}
            <div className="overflow-x-auto">
            <div className={`rounded-lg border overflow-hidden ${card}`}>
              <table className="w-full">
                <thead>
                  <tr className={darkMode ? 'bg-zinc-700' : 'bg-gray-50'}>
                    <th className={`text-left text-xs font-semibold px-4 py-3 ${t2}`}>Produto</th>
                    <th className={`text-left text-xs font-semibold px-4 py-3 ${t2}`}>Categoria</th>
                    <th className={`text-right text-xs font-semibold px-4 py-3 ${t2}`}>Preço</th>
                    <th className={`text-right text-xs font-semibold px-4 py-3 ${t2}`}>Estoque</th>
                    <th className={`text-center text-xs font-semibold px-4 py-3 ${t2}`}>SKU</th>
                  </tr>
                </thead>
                <tbody>
                  {produtos.map(p => (
                    <tr key={p.id} className={`border-t ${darkMode ? 'border-zinc-700 hover:bg-zinc-700/50' : 'border-gray-100 hover:bg-gray-50'}`}>
                      <td className={`px-4 py-2.5 text-sm ${t1}`}>
                        <div className="flex items-center gap-2">
                          {p.imagem_url && <img src={p.imagem_url} alt="" className="w-8 h-8 rounded object-cover" />}
                          <span className="truncate max-w-xs">{p.nome}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        {editando === p.id ? (
                          <div className="flex items-center gap-1">
                            <input value={novaCategoria} onChange={e => setNovaCategoria(e.target.value)}
                              className={`text-xs px-2 py-1 rounded border w-28 ${darkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'border-gray-300'}`} />
                            <button onClick={() => salvarCategoria(p.id)} className="text-green-500"><Check size={14} /></button>
                            <button onClick={() => setEditando(null)} className="text-red-500"><X size={14} /></button>
                          </div>
                        ) : (
                          <button onClick={() => { setEditando(p.id); setNovaCategoria(p.categoria || ''); }}
                            className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${p.categoria ? (darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-100 text-gray-700') : 'bg-yellow-100 text-yellow-700'}`}>
                            {p.categoria || 'Sem categoria'} <Edit2 size={10} />
                          </button>
                        )}
                      </td>
                      <td className={`px-4 py-2.5 text-sm text-right font-medium ${t1}`}>R$ {parseFloat(p.preco || 0).toFixed(2)}</td>
                      <td className={`px-4 py-2.5 text-sm text-right ${p.estoque > 0 ? 'text-green-500' : 'text-red-500'}`}>{p.estoque}</td>
                      <td className={`px-4 py-2.5 text-xs text-center ${t2}`}>{p.sku}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>

            {/* Paginação */}
            <div className="flex items-center justify-between mt-4">
              <p className={`text-xs ${t2}`}>{total} produtos</p>
              <div className="flex items-center gap-2">
                <button disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)}
                  className={`p-1.5 rounded ${pagina <= 1 ? 'opacity-30' : darkMode ? 'hover:bg-zinc-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'}`}>
                  <ChevronLeft size={16} className={darkMode ? 'text-gray-300' : 'text-gray-700'} />
                </button>
                <span className={`text-xs px-3 py-1 rounded ${darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>{pagina} / {totalPaginas}</span>
                <button disabled={pagina >= totalPaginas} onClick={() => setPagina(p => p + 1)}
                  className={`p-1.5 rounded ${pagina >= totalPaginas ? 'opacity-30' : darkMode ? 'hover:bg-zinc-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'}`}>
                  <ChevronRight size={16} className={darkMode ? 'text-gray-300' : 'text-gray-700'} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Produtos;
